// frontend/src/components/games/CharadesGame.jsx
// Charades - Act and Guess Game

import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

const CharadesGame = ({ session, onLeave, onRefresh }) => {
  const [currentWord, setCurrentWord] = useState('');
  const [guess, setGuess] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  const [roundNumber, setRoundNumber] = useState(0);
  const [currentActorIndex, setCurrentActorIndex] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [guesses, setGuesses] = useState([]);
  const [showWord, setShowWord] = useState(false);

  const words = [
    { word: 'Swimming', category: 'Sport', difficulty: 'Easy' },
    { word: 'Cooking Pasta', category: 'Activity', difficulty: 'Easy' },
    { word: 'Playing Guitar', category: 'Music', difficulty: 'Easy' },
    { word: 'Reading Book', category: 'Activity', difficulty: 'Easy' },
    { word: 'Driving Car', category: 'Activity', difficulty: 'Easy' },
    { word: 'Dancing', category: 'Activity', difficulty: 'Easy' },
    { word: 'Taking Photo', category: 'Activity', difficulty: 'Medium' },
    { word: 'Brushing Teeth', category: 'Daily Life', difficulty: 'Easy' },
    { word: 'Playing Basketball', category: 'Sport', difficulty: 'Easy' },
    { word: 'Drinking Coffee', category: 'Daily Life', difficulty: 'Easy' },
    { word: 'Walking Dog', category: 'Activity', difficulty: 'Easy' },
    { word: 'Riding Bicycle', category: 'Activity', difficulty: 'Easy' },
    { word: 'Eating Pizza', category: 'Food', difficulty: 'Easy' },
    { word: 'Sleeping', category: 'Daily Life', difficulty: 'Easy' },
    { word: 'Running Marathon', category: 'Sport', difficulty: 'Medium' },
    { word: 'Singing', category: 'Music', difficulty: 'Easy' },
    { word: 'Typing', category: 'Work', difficulty: 'Easy' },
    { word: 'Washing Dishes', category: 'Daily Life', difficulty: 'Easy' },
    { word: 'Playing Chess', category: 'Game', difficulty: 'Medium' },
    { word: 'Painting', category: 'Art', difficulty: 'Medium' }
  ];

  useEffect(() => {
    if (gameStarted && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameStarted) {
      handleRoundEnd(false);
    }
  }, [timeLeft, gameStarted]);

  const handleStartGame = async () => {
    try {
      await api.post(`/funzone/session/${session.sessionCode}/start`);
      setGameStarted(true);
      startNewRound();
      onRefresh();
    } catch (error) {
      console.error('Start error:', error);
      alert(error.response?.data?.message || 'Failed to start game');
    }
  };

  const startNewRound = () => {
    const randomWord = words[Math.floor(Math.random() * words.length)];
    setCurrentWord(randomWord);
    setShowWord(false);
    setTimeLeft(60);
    setRoundNumber(prev => prev + 1);
    setGuesses([]);
    setGuess('');
    
    // Move to next actor
    setCurrentActorIndex((currentActorIndex + 1) % session.players.length);
  };

  const handleRevealWord = () => {
    setShowWord(true);
  };

  const handleGuess = async (e) => {
    e.preventDefault();
    
    if (!guess.trim()) return;
    
    const isCorrect = guess.toLowerCase().includes(currentWord.word.toLowerCase().split(' ')[0]) ||
                      currentWord.word.toLowerCase().includes(guess.toLowerCase());
    
    setGuesses([...guesses, { 
      text: guess, 
      isCorrect,
      player: 'You'
    }]);
    
    if (isCorrect) {
      await handleRoundEnd(true);
    }
    
    setGuess('');
  };

  const handleRoundEnd = async (wasGuessed) => {
    if (wasGuessed) {
      // Award points to guesser and actor
      const basePoints = 100;
      const timeBonus = Math.floor(timeLeft * 2);
      const totalPoints = basePoints + timeBonus;
      
      try {
        // Points to guesser
        await api.put(`/funzone/session/${session.sessionCode}/score`, {
          userId: session.players[0].user._id,
          points: totalPoints
        });
        
        // Points to actor
        await api.put(`/funzone/session/${session.sessionCode}/score`, {
          userId: session.players[currentActorIndex].user._id,
          points: 50
        });
        
        setTimeout(() => {
          if (roundNumber < 12) {
            startNewRound();
          } else {
            handleFinishGame();
          }
        }, 3000);
        
      } catch (error) {
        console.error('Score error:', error);
      }
    } else {
      // Time's up, no points
      setTimeout(() => {
        if (roundNumber < 12) {
          startNewRound();
        } else {
          handleFinishGame();
        }
      }, 2000);
    }
  };

  const handleSkip = () => {
    handleRoundEnd(false);
  };

  const handleFinishGame = async () => {
    try {
      await api.post(`/funzone/session/${session.sessionCode}/finish`);
      alert('Game finished! Check the leaderboard.');
      onLeave();
    } catch (error) {
      console.error('Finish error:', error);
    }
  };

  const getCurrentActor = () => {
    return session.players[currentActorIndex];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-900 via-red-700 to-pink-600 text-white p-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex justify-between items-center bg-gray-800 rounded-lg p-4">
          <div>
            <h1 className="text-3xl font-bold">🎭 Charades</h1>
            <p className="text-gray-400">Session: #{session.sessionCode}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-500">
                Round {roundNumber}/12
              </div>
              <div className="text-sm text-gray-400">{timeLeft}s left</div>
            </div>
            <button
              onClick={onLeave}
              className="px-4 py-2 bg-red-600 rounded-lg hover:bg-red-700"
            >
              Leave Game
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Players Panel */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-xl font-bold mb-4">👥 Players</h3>
          
          <div className="space-y-3 mb-6">
            {session.players.map((player, idx) => (
              <div 
                key={idx} 
                className={`rounded-lg p-4 ${
                  idx === currentActorIndex && gameStarted
                    ? 'bg-orange-600 ring-4 ring-orange-300'
                    : 'bg-gray-700'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-semibold">
                      {player.nickname || player.user?.name}
                      {idx === currentActorIndex && gameStarted && (
                        <div className="text-xs mt-1">🎭 Acting Now</div>
                      )}
                    </div>
                  </div>
                  <div className="text-yellow-500 font-bold text-lg">
                    {player.score}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {!gameStarted && session.status === 'waiting' && (
            <button
              onClick={handleStartGame}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-bold hover:bg-green-700"
            >
              Start Game
            </button>
          )}
          
          {gameStarted && (
            <button
              onClick={handleFinishGame}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-bold hover:bg-purple-700"
            >
              Finish Game
            </button>
          )}

          {/* Rules */}
          <div className="mt-6 bg-blue-900 rounded-lg p-4">
            <h4 className="font-bold mb-2">📋 Rules:</h4>
            <ul className="text-sm space-y-1 text-gray-300">
              <li>• Actor: Act without speaking</li>
              <li>• Team: Guess what's being acted</li>
              <li>• Correct guess: Points to all</li>
              <li>• Faster = more points</li>
              <li>• 60 seconds per round</li>
            </ul>
          </div>
        </div>

        {/* Main Game Area */}
        <div className="lg:col-span-2 space-y-6">
          {/* Actor's Card */}
          {gameStarted && (
            <div className="bg-gray-800 rounded-lg p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2">
                  🎭 {getCurrentActor()?.nickname || getCurrentActor()?.user?.name} is acting!
                </h3>
                <p className="text-gray-400">Everyone else: Guess what they're acting!</p>
              </div>

              {!showWord ? (
                <div className="text-center">
                  <p className="text-xl mb-6 text-gray-300">
                    Actor: Click below to see your word. Don't show others!
                  </p>
                  <button
                    onClick={handleRevealWord}
                    className="px-8 py-4 bg-orange-600 text-white rounded-lg font-bold text-xl hover:bg-orange-700"
                  >
                    👁️ Reveal My Word
                  </button>
                </div>
              ) : (
                <div className="bg-orange-900 rounded-lg p-8 border-4 border-orange-500">
                  <p className="text-center text-sm text-gray-400 mb-2">Your word to act:</p>
                  <h2 className="text-6xl font-bold text-center mb-4">
                    {currentWord.word}
                  </h2>
                  <div className="flex justify-center gap-4 text-sm">
                    <span className="px-3 py-1 bg-gray-700 rounded-full">
                      {currentWord.category}
                    </span>
                    <span className="px-3 py-1 bg-gray-700 rounded-full">
                      {currentWord.difficulty}
                    </span>
                  </div>
                  <p className="text-center text-gray-400 mt-4 text-sm">
                    🤫 Act it out without speaking!
                  </p>
                </div>
              )}

              {showWord && (
                <div className="mt-6 flex gap-3">
                  <button
                    onClick={handleSkip}
                    className="flex-1 px-6 py-3 bg-gray-600 rounded-lg hover:bg-gray-700 font-bold"
                  >
                    ⏭️ Skip (Too Hard)
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Guessing Area */}
          {gameStarted && showWord && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4">💭 Make Your Guess!</h3>
              
              {/* Guess History */}
              <div className="bg-gray-700 rounded-lg p-4 mb-4 h-48 overflow-y-auto">
                {guesses.length === 0 ? (
                  <p className="text-center text-gray-400 py-12">
                    No guesses yet...
                  </p>
                ) : (
                  <div className="space-y-2">
                    {guesses.map((g, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg ${
                          g.isCorrect ? 'bg-green-600' : 'bg-gray-600'
                        }`}
                      >
                        <span className="font-semibold">{g.player}: </span>
                        <span>{g.text}</span>
                        {g.isCorrect && <span className="ml-2">✓ CORRECT!</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Guess Input */}
              <form onSubmit={handleGuess} className="flex gap-3">
                <input
                  type="text"
                  value={guess}
                  onChange={(e) => setGuess(e.target.value)}
                  placeholder="Type your guess..."
                  className="flex-1 px-6 py-4 bg-gray-700 rounded-lg text-white text-xl focus:outline-none focus:ring-4 focus:ring-orange-500"
                  autoFocus
                />
                <button
                  type="submit"
                  className="px-8 py-4 bg-orange-600 rounded-lg hover:bg-orange-700 font-bold text-xl"
                >
                  Guess!
                </button>
              </form>

              {/* Scoring Info */}
              <div className="mt-4 bg-blue-900 rounded-lg p-4">
                <h4 className="font-bold mb-2">💰 Scoring:</h4>
                <div className="text-sm space-y-1 text-gray-300">
                  <div>• Guesser: 100 + time bonus (up to 120 pts)</div>
                  <div>• Actor: 50 points</div>
                  <div>• Faster guess = more bonus points!</div>
                </div>
              </div>
            </div>
          )}

          {/* Waiting Screen */}
          {!gameStarted && session.status === 'waiting' && (
            <div className="bg-gray-800 rounded-lg p-12 text-center">
              <h2 className="text-4xl font-bold mb-4">🎭 Charades!</h2>
              <p className="text-xl text-gray-400 mb-8">
                Act it out, guess it right!
              </p>

              <div className="bg-gray-700 rounded-lg p-6 mb-6">
                <h3 className="text-2xl font-bold mb-4">How to Play:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  <div className="bg-gray-600 rounded-lg p-4">
                    <div className="text-3xl mb-2">🎭</div>
                    <div className="font-bold mb-1">Actor:</div>
                    <div className="text-sm text-gray-300">
                      1. See your word<br/>
                      2. Act it out (no speaking!)<br/>
                      3. Help your team guess
                    </div>
                  </div>
                  <div className="bg-gray-600 rounded-lg p-4">
                    <div className="text-3xl mb-2">💭</div>
                    <div className="font-bold mb-1">Guessers:</div>
                    <div className="text-sm text-gray-300">
                      1. Watch the actor<br/>
                      2. Type your guesses<br/>
                      3. First correct wins!
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-orange-900 rounded-lg p-4">
                  <div className="text-3xl mb-2">🏃</div>
                  <div className="text-sm font-semibold">Actions</div>
                </div>
                <div className="bg-orange-900 rounded-lg p-4">
                  <div className="text-3xl mb-2">🎨</div>
                  <div className="text-sm font-semibold">Activities</div>
                </div>
                <div className="bg-orange-900 rounded-lg p-4">
                  <div className="text-3xl mb-2">⚽</div>
                  <div className="text-sm font-semibold">Sports</div>
                </div>
              </div>

              <div className="bg-red-900 rounded-lg p-4">
                <h4 className="font-bold mb-2">⚠️ Important:</h4>
                <p className="text-sm text-gray-300">
                  No talking! No mouthing words! Use gestures and actions only!
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CharadesGame;
